import { EventEmitter, Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRouteSnapshot, CanDeactivate } from "@angular/router";
import { ClareDialogComponent, IDialogConfig } from "@app/shared/components/clare-dialog/clare-dialog.component";
import { MatIconLoaderService } from "@app/shared/services/mat-icon-loader.service";
import { Observable, of, switchMap, take } from "rxjs";

interface WarningDialogConfig {
    title: string;
}

export interface ComponentCanDeactivate {
    canDeactivate: () => boolean | Observable<boolean>;
    warningDialogConfig: WarningDialogConfig;
}

@Injectable({
    providedIn: "root",
})
export class PendingChangesGuard implements CanDeactivate<ComponentCanDeactivate> {
    dialogRef: MatDialogRef<any>;
    constructor(private readonly dialog: MatDialog, private readonly matIconLoader: MatIconLoaderService) {
        matIconLoader.importIcons([[`cc-exclamation`, `exclamation`]]);
    }

    canDeactivate(component: ComponentCanDeactivate, currentRoute: ActivatedRouteSnapshot) {
        return !component || component.canDeactivate()
            ? true
            : // NOTE: this warning message will only be shown when navigating elsewhere within your angular app;
              // when navigating away from your angular app, the browser will show a generic warning message
              // see http://stackoverflow.com/a/42207299/7307355
              // eslint-disable-next-line no-alert
              //   confirm("WARNING: You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes.");
              this.leavePage(component);
    }

    leavePage(component?: ComponentCanDeactivate) {
        const emitter = new EventEmitter();
        const cancelEmitter = new EventEmitter();
        const config = this.setupLeavePageDialog(emitter, cancelEmitter, component.warningDialogConfig);
        const returnObs$ = emitter.pipe(
            take(1),
            switchMap(result => {
                this.dialog.closeAll();
                return of(true);
            })
        );
        cancelEmitter.subscribe(result => {
            this.dialog.closeAll();
        });
        returnObs$.subscribe();
        this.openConfirmDialog(config);

        return returnObs$;
    }

    openConfirmDialog(config): Observable<any> {
        // Check if a dialog is already open
        if (this.dialogRef && this.dialogRef.componentInstance) {
            // A dialog is already open, return an observable of null
            return new Observable(observer => {
                observer.next(null);
                observer.complete();
            });
        }
        // No dialog is open, open a new one
        this.dialogRef = this.dialog.open(ClareDialogComponent, config);
        return this.dialogRef.afterClosed().pipe(take(1));
    }

    private setupLeavePageDialog(
        emitter: EventEmitter<void>,
        cancelEmitter: EventEmitter<void>,
        dialogConfig: WarningDialogConfig
    ): IDialogConfig {
        return {
            id: "leave-page",
            data: {
                icon: "cc-exclamation",
                title: dialogConfig.title,
                message: "Any changes made during this operation may affect the overall performance of the panel",
                loading: false,
                buttons: [
                    {
                        text: "Stay",
                        buttonColor: "primary",
                        method: cancelEmitter,
                    },
                    {
                        text: "Leave",
                        method: emitter,
                    },
                ],
            },
            disableClose: false,
        };
    }
}
